import _slicedToArray from "@babel/runtime/helpers/esm/slicedToArray";
import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import app from 'flarum/forum/app';
import { purgeSnapshots, readSnapshot, serialize, writeSnapshot, signedOutSince } from '../utils/snapshot';

/** Janela de mensagens carregada de um canal e o estado de paginação. */

const PAGE_SIZE = 50;

/**
 * Tamanho de página e limite para preencher a lacuna até uma mensagem mais antiga
 * que a janela. 100 é o máximo do endpoint; vinte páginas são duas mil mensagens,
 * acima disso o salto desiste em vez de travar o navegador.
 */
const GAP_PAGE_SIZE = 100;
const GAP_MAX_PAGES = 20;

/** Quantos canais vistos recentemente o snapshot local guarda. */
const SNAPSHOT_CHANNELS = 3;

/** Pausa antes de gravar o snapshot; ver scheduleSnapshot(). */
const SNAPSHOT_DEBOUNCE_MS = 1500;

/** Quando esta aba abriu: uma saída da conta depois disso a impede de gravar. */
const BOOTED_AT = Date.now();

/**
 * Tamanho da coleção informado numa resposta paginada, ou null. `Store#find`
 * pendura o documento cru no array retornado, e o json-api-server põe o total em
 * `meta.page.total`; assim um `limit: 1` também diz quantos existem.
 */
function readTotal(results) {
  var _payload;
  const total = results == null || (_payload = results.payload) == null || (_payload = _payload.meta) == null || (_payload = _payload.page) == null ? void 0 : _payload.total;
  return typeof total === 'number' ? total : null;
}

/**
 * Fonte única de verdade da UI do chat. Os componentes leem daqui e ficam só de
 * apresentação; paginação, leitura, envio otimista e reconciliação de realtime num
 * só lugar mantêm a gaveta e a página em sincronia.
 */
export default class ChatState {
  constructor() {
    /** Canais da sidebar, atividade mais recente primeiro. */
    _defineProperty(this, "channels", []);
    _defineProperty(this, "channelsLoading", false);
    _defineProperty(this, "channelsLoaded", false);
    /**
     * Request de canais em andamento, para chamadas simultâneas dividirem a ida ao
     * servidor. Devolver `this.channels` não basta: no início ele ainda está vazio.
     */
    _defineProperty(this, "channelsRequest", null);
    /**
     * Se os rascunhos já foram buscados na sessão. O composer os grava ao mudar, então
     * a cópia em memória é a mais nova; buscar de novo poderia sobrescrevê-la.
     */
    _defineProperty(this, "draftsLoaded", false);
    _defineProperty(this, "draftsRequest", null);
    /** A sidebar veio do snapshot local; rebusca no primeiro uso. */
    _defineProperty(this, "channelsStale", false);
    /** Canais cuja prévia de fixada veio do snapshot local. */
    _defineProperty(this, "pinnedStale", new Set());
    /** Prévias de fixada sendo buscadas, para prefetch e view dividirem o request. */
    _defineProperty(this, "pinnedInFlight", new Map());
    /** Gravação de snapshot pendente (debounce), se houver. */
    _defineProperty(this, "snapshotTimer", null);
    /**
     * Canais vistos na sessão, mais recente primeiro: aqueles cujo histórico o
     * snapshot guarda.
     */
    _defineProperty(this, "recentChannelIds", []);
    /** Janelas de mensagens carregadas, por id de canal. */
    _defineProperty(this, "streams", {});
    /** Tópicos carregados, por id de canal. */
    _defineProperty(this, "threads", {});
    /** Janelas de mensagens dos tópicos abertos, por id de tópico. */
    _defineProperty(this, "threadStreams", {});
    /**
     * Fixada mais recente por canal, para a barra acima do stream. Buscada à parte
     * porque costuma estar bem acima da janela. `null` é "perguntado, nada fixado";
     * `undefined` é "ainda não perguntado".
     */
    _defineProperty(this, "pinnedPreviews", {});
    /**
     * Quantas fixadas cada canal tem, segundo a última contagem do servidor (lida do
     * meta de paginação do request da prévia). Separado de `pinnedPreviews`: um canal
     * pode ter total sem prévia se o request da prévia falhou.
     */
    _defineProperty(this, "pinnedTotals", {});
    /** Rascunhos do composer, por `channelId` ou `channelId:threadId`. */
    _defineProperty(this, "drafts", {});
    /** Indicadores de digitação, por id de canal e depois de usuário. */
    _defineProperty(this, "typing", {});
    /** Canal e tópico em exibição. */
    _defineProperty(this, "activeChannelId", null);
    _defineProperty(this, "activeThreadId", null);
    /**
     * Se o painel de fixadas está aberto. Divide o slot direito com o painel de
     * tópico; abrir um fecha o outro (ver togglePinned() / setActiveThread()).
     */
    _defineProperty(this, "showPinned", false);
    /**
     * Se a gaveta mostra a busca em vez da conversa. Só na gaveta: na página a busca é
     * a rota `chat.search`, e navegar a partir da gaveta a fecharia. Divide o slot de
     * sobreposição com os painéis de tópico e de fixadas.
     */
    _defineProperty(this, "showSearch", false);
    /**
     * Gaveta aberta/recolhida. Persistido, para sobreviver ao reload; altere pelos
     * setters abaixo, ou a mudança não é lembrada.
     */
    _defineProperty(this, "drawerOpen", false);
    _defineProperty(this, "drawerCollapsed", false);
    /**
     * A gaveta fechou para ir à tela cheia, não foi dispensada. Fechada no X fica
     * fechada; a que só saiu da frente volta ao deixar a página.
     */
    _defineProperty(this, "drawerSuspended", false);
    /** Modo de seleção, para citar/copiar/mover. */
    _defineProperty(this, "selecting", false);
    _defineProperty(this, "selected", new Set());
    /**
     * Mensagem sendo respondida ou editada, por escopo do composer (a mesma chave
     * `channelId` / `channelId:threadId` dos rascunhos). Por escopo porque o canal e
     * um tópico aberto desenham dois composers sobre este estado.
     */
    _defineProperty(this, "replyTargets", {});
    _defineProperty(this, "editTargets", {});
    /**
     * Escopos cujo próximo envio deve abrir um tópico novo. Responder e ramificar
     * preparam igual (ambos põem um alvo em `replyTargets`), então isto os distingue.
     */
    _defineProperty(this, "branchTargets", {});
    /**
     * Anexos preparados e ainda não enviados, por conversa (`draftKey`), para um
     * anexo de uma DM não sair na próxima mensagem de outro canal.
     */
    _defineProperty(this, "pendingUploadsByKey", {});
    /**
     * Linhas otimistas aguardando resposta, por token gerado no cliente. Aparecem
     * esmaecidas e são trocadas no lugar quando dá certo.
     */
    _defineProperty(this, "pending", new Map());
    _defineProperty(this, "pendingSeq", 0);
    // ── Payload de boot ──
    /** Se o payload de boot já foi lido; só vale uma vez. */
    _defineProperty(this, "bootHydrated", false);
    /**
     * Quem quer saber quando a lista de membros de um canal muda. A aba de membros é a
     * única superfície desenhada de uma lista que o payload de realtime não carrega.
     * Listeners em vez de contador no estado: um modal fechado não custa nada.
     */
    _defineProperty(this, "membershipListeners", new Set());
    /**
     * Lista de tópicos do canal no slot de sobreposição da gaveta. Só na gaveta, como
     * a busca: na página é a rota `chat.threads`, e navegar fecharia a gaveta.
     */
    _defineProperty(this, "showThreads", false);
    _defineProperty(this, "draftTimer", null);
    _defineProperty(this, "typingSentAt", 0);
  }
  // ── Persistência da gaveta ──

  /** Por usuário, para um navegador compartilhado não restaurar o canal de outra conta. */
  storageKey() {
    var _app$session$user;
    const id = (_app$session$user = app.session.user) == null ? void 0 : _app$session$user.id();
    return id ? "ramon-chat.drawer.".concat(id) : null;
  }

  /**
   * Lê o estado da gaveta salvo pela visita anterior. Retorna se ela deve abrir,
   * para quem chama decidir se carrega a lista de canais.
   */
  restoreDrawer() {
    const key = this.storageKey();
    if (!key) return false;
    try {
      const raw = localStorage.getItem(key);
      if (!raw) return false;
      const saved = JSON.parse(raw);
      this.drawerOpen = Boolean(saved.open);
      this.drawerCollapsed = Boolean(saved.collapsed);
      // Persistido para um reload na página cheia não perder a reabertura devida.
      this.drawerSuspended = Boolean(saved.suspended);
      if (saved.channelId) {
        this.activeChannelId = Number(saved.channelId);
      }
      return this.drawerOpen;
    } catch (_unused) {
      // Navegação privada, erro de cota ou lixo editado à mão; não vale quebrar o boot.
      return false;
    }
  }
  persistDrawer() {
    this.persistDrawerCookie();
    const key = this.storageKey();
    if (!key) return;
    try {
      localStorage.setItem(key, JSON.stringify({
        open: this.drawerOpen,
        collapsed: this.drawerCollapsed,
        suspended: this.drawerSuspended,
        channelId: this.activeChannelId
      }));
    } catch (_unused2) {
      // Não fatal, como acima.
    }
  }

  /**
   * Espelha "gaveta aberta neste canal" num cookie que o servidor lê. O
   * localStorage é invisível ao render, e Content\PreloadChat usa o cookie para pôr
   * a lista e a conversa da gaveta no payload de boot de toda página.
   *
   * Só o id do canal, nunca conteúdo; ausente com a gaveta fechada, para o servidor
   * não trabalhar à toa.
   */
  persistDrawerCookie() {
    try {
      const secure = location.protocol === 'https:' ? '; Secure' : '';
      if (this.drawerOpen && !this.drawerSuspended) {
        var _this$activeChannelId;
        document.cookie = "ramon_chat_drawer=".concat((_this$activeChannelId = this.activeChannelId) != null ? _this$activeChannelId : 0, "; path=/; max-age=31536000; SameSite=Lax").concat(secure);
      } else {
        document.cookie = "ramon_chat_drawer=; path=/; max-age=0; SameSite=Lax".concat(secure);
      }
    } catch (_unused3) {
      // Não fatal.
    }
  }
  setDrawerOpen(open) {
    this.drawerOpen = open;

    // Abrir ou fechar diretamente resolve a questão, então uma suspensão pendente
    // perde a validade. É isto que torna o X definitivo.
    this.drawerSuspended = false;
    this.persistDrawer();
  }

  /**
   * Fecha a gaveta porque a página cheia assume a tela. Diferente de
   * `setDrawerOpen(false)`, que limparia a suspensão, e de `suspendDrawer()`, que
   * criaria uma: chegar à página por outra rota não garante reabertura na saída.
   */
  hideDrawerForPage() {
    this.drawerOpen = false;
    this.persistDrawer();
  }

  /**
   * Fecha a gaveta devendo reabri-la. Usado ao passar a conversa para a página
   * cheia, o único caso em que a gaveta some sem o usuário dispensá-la.
   */
  suspendDrawer() {
    this.drawerOpen = false;
    this.drawerSuspended = true;
    this.persistDrawer();
  }

  /**
   * Reabre uma gaveta suspensa e diz se havia uma. Consumir a flag aqui torna
   * "restaurar no máximo uma vez" uma propriedade do estado.
   */
  resumeDrawer() {
    if (!this.drawerSuspended) return false;
    this.drawerSuspended = false;
    this.drawerOpen = true;
    this.persistDrawer();
    return true;
  }
  setDrawerCollapsed(collapsed) {
    this.drawerCollapsed = collapsed;
    this.persistDrawer();
  }

  /** Registra o canal em exibição, para o reload reabrir o mesmo. */
  setActiveChannel(channelId) {
    // A seleção é do canal em que foi feita: levada para outro, "mover" ou
    // "citar" agiriam também sobre mensagens que já não estão na tela.
    if (channelId !== this.activeChannelId) {
      this.selecting = false;
      this.selected.clear();
    }
    this.activeChannelId = channelId;
    if (channelId !== null) {
      this.recentChannelIds = [channelId, ...this.recentChannelIds.filter(id => id !== channelId)].slice(0, SNAPSHOT_CHANNELS);
    }
    this.persistDrawer();
  }
  /**
   * Preenche o estado com o payload que Content\PreloadChat pôs na página, de forma
   * síncrona, para o primeiro frame já ser o chat pronto.
   *
   * Chamado no initializer, antes de montar: `app.data` vem de `Application#load()`,
   * que roda antes dos initializers, mas `app.forum` e `app.session` ainda não
   * existem, então nada aqui pode tocá-los.
   *
   * Cada passo é opcional e protegido; o que o servidor não produziu, ou um stream
   * já carregado, fica para o caminho assíncrono normal.
   */
  hydrateFromBoot() {
    var _data;
    if (this.bootHydrated) return;
    this.bootHydrated = true;
    const boot = (_data = app.data) == null ? void 0 : _data.ramonChat;
    if (!boot) return;
    try {
      var _boot$channelId, _boot$threadId;
      this.hydrateChannels(boot.channels);
      this.hydrateDrafts(boot.drafts);
      const channelId = Number((_boot$channelId = boot.channelId) != null ? _boot$channelId : 0);
      if (channelId > 0) {
        this.hydrateStream(this.stream(channelId), boot.messages, channelId);
        this.hydratePinned(channelId, boot.pinned);
      }
      const threadId = Number((_boot$threadId = boot.threadId) != null ? _boot$threadId : 0);
      if (threadId > 0) {
        this.hydrateStream(this.threadStream(threadId), boot.threadMessages);
      }
    } catch (_unused4) {
      // Um preload que quebrasse o chat seria pior que o flash que evita; o que não foi
      // hidratado é buscado como sempre.
    }
  }

  /**
   * Semeia o que o payload de boot não trouxe a partir da cópia que a última visita
   * deixou no localStorage: a sidebar e o fim dos canais vistos recentemente.
   *
   * localStorage e não IndexedDB porque a leitura precisa ser síncrona para cair
   * antes do primeiro view(); no tamanho mantido (ver MAX_BYTES em utils/snapshot)
   * custa poucos milissegundos.
   *
   * Nada daqui é confiável além da primeira pintura: cada seção é marcada como velha
   * e trocada pela resposta do servidor em segundo plano (ver loadChannels(),
   * loadChannel() e loadPinnedPreview()). O id do usuário vem de `app.data`, não de
   * `app.session`, pelo mesmo motivo de hydrateFromBoot().
   */
  hydrateFromSnapshot() {
    const userId = this.snapshotUserId();
    if (!userId) return;
    const snapshot = readSnapshot(userId);
    if (!snapshot) return;
    try {
      if (!this.channelsLoaded) {
        this.hydrateChannels(snapshot.channels);
        if (this.channelsLoaded) this.channelsStale = true;
      }
      for (const _ref of Object.entries(snapshot.streams)) {
        var _ref2 = _slicedToArray(_ref, 2);
        const key = _ref2[0];
        const document = _ref2[1];
        const channelId = Number(key);
        if (!(channelId > 0)) continue;
        const stream = this.stream(channelId);
        if (stream.loadedInitial) continue;
        this.hydrateStream(stream, document, channelId);
        if (stream.loadedInitial) stream.stale = true;
      }
      for (const _ref3 of Object.entries(snapshot.pinned)) {
        var _ref4 = _slicedToArray(_ref3, 2);
        const key = _ref4[0];
        const document = _ref4[1];
        const channelId = Number(key);
        if (!(channelId > 0) || channelId in this.pinnedPreviews) continue;
        this.hydratePinned(channelId, document);
        if (channelId in this.pinnedPreviews) this.pinnedStale.add(channelId);
      }
    } catch (_unused5) {
      // Mesma postura do payload de boot.
    }
  }
  snapshotUserId() {
    var _data2;
    const id = (_data2 = app.data) == null || (_data2 = _data2.session) == null ? void 0 : _data2.userId;
    return typeof id === 'number' && id > 0 ? String(id) : null;
  }

  /**
   * Grava o snapshot em breve, quando as coisas assentarem. Com debounce porque as
   * mudanças vêm em rajadas, e serializar cinquenta mensagens a cada uma custaria
   * mais que o snapshot economiza.
   */
  scheduleSnapshot() {
    if (this.snapshotTimer !== null) return;
    this.snapshotTimer = window.setTimeout(() => {
      this.snapshotTimer = null;
      this.flushSnapshot();
    }, SNAPSHOT_DEBOUNCE_MS);
  }

  /**
   * Grava o snapshot agora. Também é o hook de `pagehide` e aba oculta, para uma
   * mensagem que chegou logo antes de fechar estar no próximo boot. Só o que o
   * servidor confirmou: um stream velho é do snapshot anterior.
   */
  flushSnapshot() {
    if (this.snapshotTimer !== null) {
      window.clearTimeout(this.snapshotTimer);
      this.snapshotTimer = null;
    }
    const userId = this.snapshotUserId();
    if (!userId || !this.channelsLoaded || this.channelsStale) return;

    // Outra aba saiu da conta depois que esta abriu: esta ainda acha que há
    // sessão, e gravar agora devolveria ao disco o que a saída apagou.
    if (signedOutSince(BOOTED_AT)) return;
    try {
      const streams = {};
      const pinned = {};
      for (const channelId of this.recentChannelIds) {
        const stream = this.streams[channelId];
        if (stream != null && stream.loadedInitial && !stream.loading && !stream.stale) {
          streams[channelId] = serialize(stream.messages.slice(-PAGE_SIZE));
        }
        if (channelId in this.pinnedPreviews && !this.pinnedStale.has(channelId)) {
          var _this$pinnedTotals$ch;
          const preview = this.pinnedPreviews[channelId];
          const document = preview ? serialize([preview]) : {
            data: []
          };
          pinned[channelId] = _objectSpread(_objectSpread({}, document), {}, {
            meta: {
              page: {
                total: (_this$pinnedTotals$ch = this.pinnedTotals[channelId]) != null ? _this$pinnedTotals$ch : 0
              }
            }
          });
        }
      }
      writeSnapshot(userId, {
        version: 1,
        savedAt: Date.now(),
        channels: serialize(this.channels),
        streams,
        pinned
      });
    } catch (_unused6) {
      // Não fatal: a próxima visita carrega normalmente.
    }
  }

  /** Apaga o snapshot de todas as contas; ver utils/snapshot. */
  purgeSnapshots() {
    purgeSnapshots();
  }

  /**
   * Coloca um documento JSON:API no store e devolve os models primários. `null` para
   * documento ausente ou malformado ("o servidor não respondeu este"), diferente de
   * `data` vazio, que é resposta real e não deve ser rebuscada.
   */
  pushDocument(document) {
    if (!document || typeof document !== 'object') return null;
    const data = document.data;
    if (!Array.isArray(data)) return null;
    const pushed = app.store.pushPayload(document);
    return (Array.isArray(pushed) ? pushed : [pushed]).filter(Boolean);
  }
  hydrateChannels(document) {
    if (this.channelsLoaded) return;
    const channels = this.pushDocument(document);
    if (channels === null) return;
    this.channels = channels;
    this.channelsLoaded = true;
  }
  hydrateDrafts(document) {
    if (this.draftsLoaded) return;
    const rows = document == null ? void 0 : document.data;
    if (!Array.isArray(rows)) return;
    for (const row of rows) {
      var _row$attributes;
      const _ref5 = (_row$attributes = row == null ? void 0 : row.attributes) != null ? _row$attributes : {},
        channelId = _ref5.channelId,
        threadId = _ref5.threadId,
        content = _ref5.content;
      if (channelId && content) {
        this.drafts[this.draftKey(Number(channelId), threadId ? Number(threadId) : null)] = content;
      }
    }
    this.draftsLoaded = true;
  }

  /**
   * Semeia uma janela de mensagens a partir de uma página pré-carregada. A página
   * vem do mais novo para o mais antigo e é invertida, como em `fetchInto`.
   *
   * `channelId` só para stream de canal: é de onde vem o divisor de não lidas, e
   * tópicos não têm marcador de leitura.
   */
  hydrateStream(stream, document, channelId) {
    if (stream.loadedInitial || stream.loading) return;
    const page = this.pushDocument(document);
    if (page === null) return;
    if (channelId !== undefined) {
      var _channel$lastReadMess, _channel$unreadCount;
      // Congelado aqui pelo mesmo motivo de loadChannel(): o divisor não pode seguir o
      // marcador enquanto markRead() o atualiza.
      const channel = this.channel(channelId);
      const lastRead = (_channel$lastReadMess = channel == null ? void 0 : channel.lastReadMessageId()) != null ? _channel$lastReadMess : 0;
      stream.dividerAfterId = lastRead > 0 && ((_channel$unreadCount = channel == null ? void 0 : channel.unreadCount()) != null ? _channel$unreadCount : 0) > 0 ? lastRead : null;
    }
    if (page.length < PAGE_SIZE) stream.hasMore = false;
    stream.messages = page.slice().reverse();
    this.sortStream(stream);
    stream.loadedInitial = true;
    stream.stale = false;
  }
  hydratePinned(channelId, document) {
    var _pinned$, _meta;
    if (channelId in this.pinnedPreviews) return;
    const pinned = this.pushDocument(document);
    if (pinned === null) return;
    this.pinnedPreviews[channelId] = (_pinned$ = pinned[0]) != null ? _pinned$ : null;

    // O documento pré-carregado é o mesmo que o request devolveria, com a mesma
    // contagem. Lido direto, não por `readTotal`, que espera o retorno do store.
    const total = document == null || (_meta = document.meta) == null || (_meta = _meta.page) == null ? void 0 : _meta.total;
    this.pinnedTotals[channelId] = typeof total === 'number' ? total : 0;
  }

  // ── Canais ──

  /**
   * Busca a lista de canais da sidebar, no máximo uma vez por sessão. Nada mais a
   * deixa velha: mensagens, joins, saídas e edições chegam pelo websocket e são
   * aplicadas nestes mesmos objetos, e o poller cobre o socket fora do ar.
   *
   * `force` para quando isso não vale: login/logout, ou um join pela página de
   * navegação. `errorHandler` é para o poller, que não deve mostrar o alerta do
   * core quando a sessão acabou.
   */
  async loadChannels(force, errorHandler) {
    if (force === void 0) {
      force = false;
    }
    // Aproveita um request já em andamento.
    if (this.channelsRequest) return this.channelsRequest;
    if (this.channelsLoaded && !force) {
      // Lista pintada do snapshot é servida na hora e rebuscada por trás.
      if (this.channelsStale) {
        this.channelsStale = false;
        this.loadChannels(true).catch(() => {});
      }
      return this.channels;
    }
    this.channelsLoading = true;
    this.channelsRequest = (async () => {
      try {
        const results = await app.store.find('chat-channels', {
          filter: {
            following: true
          },
          sort: '-lastMessageAt',
          page: {
            limit: 50
          }
        }, undefined, errorHandler ? {
          errorHandler
        } : {});
        this.channels = Array.isArray(results) ? results : [];
        this.channelsLoaded = true;
        this.channelsStale = false;
        this.scheduleSnapshot();
        return this.channels;
      } finally {
        this.channelsLoading = false;
        this.channelsRequest = null;
        m.redraw();
      }
    })();
    return this.channelsRequest;
  }

  /**
   * Marca um canal recém-criado como carregado e vazio, sem perguntar ao servidor.
   * Só correto para um canal que acabou de ser criado (por isso é método separado):
   * não tem mensagens nem fixada. Economiza dois requests em "Enviar mensagem" no perfil.
   */
  seedEmptyChannel(channelId) {
    const stream = this.stream(channelId);
    if (!stream.loadedInitial && !stream.loading) {
      stream.messages = [];
      stream.hasMore = false;
      stream.dividerAfterId = null;
      stream.loadedInitial = true;
    }
    if (!(channelId in this.pinnedPreviews)) {
      this.pinnedPreviews[channelId] = null;
    }
  }

  /**
   * Põe um canal no topo da sidebar, se ainda não estiver. A lista é por última
   * atividade, e uma conversa recém-iniciada é a mais recente.
   */
  rememberChannel(channel) {
    if (this.channels.some(existing => existing.id() === channel.id())) return;
    this.channels.unshift(channel);
  }

  /**
   * Canal que não existe mais para este leitor: excluído ou fora de alcance. Sai da
   * lista, do stream em cache, da fixada e do snapshot, para um reload não trazê-lo
   * de volta; quem o vê sai dele com um aviso em vez de ficar numa página que só
   * responde 404.
   */
  channelGone(channelId) {
    var _m$route$get;
    const wasOpen = this.activeChannelId === channelId && ((_m$route$get = m.route.get()) != null ? _m$route$get : '').includes("/chat/c/".concat(channelId));
    this.forgetChannel(channelId);
    if (wasOpen) m.route.set(app.route('chat.index'));

    // Só para quem estava olhando: avisar cada exclusão inundaria quem está em muitos
    // canais, um administrador sobretudo.
    if (wasOpen) {
      app.alerts.show({
        type: 'warning'
      }, app.translator.trans('ramon-chat.forum.channel.gone'));
    }
    m.redraw();
  }

  /**
   * Handler de erro para requests de um canal: 403 ou 404 quer dizer que o canal
   * sumiu para o leitor, e `channelGone` resolve em silêncio no lugar do alerta
   * genérico do core. O resto segue adiante.
   */
  goneHandler(channelId) {
    return error => {
      if ((error == null ? void 0 : error.status) === 403 || (error == null ? void 0 : error.status) === 404) {
        this.channelGone(channelId);
        return;
      }
      return false;
    };
  }

  /**
   * Tira o canal da sidebar, e da tela se estava aberto. Para uma participação que
   * terminou (saída, remoção, canal privado que deixou de ser visível). O registro
   * no store fica; a próxima busca diz o que ainda é permitido.
   */
  forgetChannel(channelId) {
    this.channels = this.channels.filter(channel => Number(channel.id()) !== channelId);

    // Sai também do snapshot local: quem foi removido de um canal privado não deve
    // achar o histórico dele no disco.
    delete this.streams[channelId];
    delete this.pinnedPreviews[channelId];
    delete this.pinnedTotals[channelId];
    this.recentChannelIds = this.recentChannelIds.filter(id => id !== channelId);
    this.scheduleSnapshot();
    if (this.activeChannelId === channelId) {
      this.closeOverlays();
      this.setActiveChannel(null);
    }
  }
  /** Retorna a função que cancela a inscrição. */
  onMembershipChange(listener) {
    this.membershipListeners.add(listener);
    return () => {
      this.membershipListeners.delete(listener);
    };
  }
  notifyMembershipChange(channelId) {
    this.membershipListeners.forEach(listener => {
      try {
        listener(channelId);
      } catch (_unused7) {
        // A falha de um listener não pode impedir os outros de serem avisados.
      }
    });
  }

  /**
   * Aquece tudo o que abrir um canal pediria, sem esperar. Chamado no hover e no
   * foco, para o clique cair numa conversa já carregada em navegações do cliente,
   * que não têm payload de boot. As duas chamadas são idempotentes.
   */
  prefetchChannel(channelId) {
    this.loadChannel(channelId).catch(() => {});
    this.loadPinnedPreview(channelId).catch(() => {});
  }

  /**
   * Apaga a lista de canais em cache para a próxima leitura rebuscar. Para mudanças
   * que invalidam a lista inteira: troca de conta, ou join feito onde o model não existe.
   */
  invalidateChannels() {
    this.channelsLoaded = false;
  }
  channel(id) {
    var _ref6;
    if (id === null) return null;
    return (_ref6 = app.store.getById('chat-channels', String(id))) != null ? _ref6 : null;
  }

  /** Canais de categoria, para a seção "Canais" da sidebar. */
  categoryChannels() {
    return this.channels.filter(c => c.isCategory());
  }

  /** Canais diretos e de grupo, para a seção "Mensagens diretas". */
  directChannels() {
    return this.channels.filter(c => c.isDirect());
  }

  /**
   * Canais com não lidas e quantas menções pendentes. Prefere a lista carregada, que
   * reflete os pushes na hora (`bumpChannel` a incrementa). Sem ela, usa os
   * contadores serializados no usuário da sessão, presentes desde a primeira pintura.
   */
  unreadSummary() {
    var _user$attribute, _user$attribute2, _user$attribute3;
    if (this.channelsLoaded && this.channels.length > 0) {
      let channels = 0;
      let messages = 0;
      let mentions = 0;
      for (const channel of this.channels) {
        var _channel$unreadMentio;
        // Canais silenciados não têm badge e não entram na contagem do header.
        if (channel.isMuted()) continue;
        if (channel.hasUnread()) {
          var _channel$unreadCount2;
          channels++;
          messages += (_channel$unreadCount2 = channel.unreadCount()) != null ? _channel$unreadCount2 : 0;
        }
        if (channel.hasUnreadMentions()) mentions += (_channel$unreadMentio = channel.unreadMentionsCount()) != null ? _channel$unreadMentio : 0;
      }
      return {
        channels,
        messages,
        mentions
      };
    }

    // Antes da lista carregar (o normal com o chat ainda fechado) só existem os
    // contadores serializados; o realtime os mantém (ver bumpUnreadCounters()).
    const user = app.session.user;
    return {
      channels: Number((_user$attribute = user == null ? void 0 : user.attribute('chatUnreadChannelsCount')) != null ? _user$attribute : 0),
      messages: Number((_user$attribute2 = user == null ? void 0 : user.attribute('chatUnreadMessagesCount')) != null ? _user$attribute2 : 0),
      mentions: Number((_user$attribute3 = user == null ? void 0 : user.attribute('chatUnreadMentionsCount')) != null ? _user$attribute3 : 0)
    };
  }

  /**
   * Move os contadores serializados do ator. O badge do header e o ponto da
   * navegação os usam quando a lista de canais não foi carregada.
   */
  bumpUnreadCounters(messages, mentions, newChannel) {
    const user = app.session.user;
    if (!user) return;
    const at = key => {
      var _user$attribute4;
      return Number((_user$attribute4 = user.attribute(key)) != null ? _user$attribute4 : 0);
    };
    user.pushAttributes({
      chatUnreadMessagesCount: Math.max(0, at('chatUnreadMessagesCount') + messages),
      chatUnreadMentionsCount: Math.max(0, at('chatUnreadMentionsCount') + mentions),
      chatUnreadChannelsCount: Math.max(0, at('chatUnreadChannelsCount') + (newChannel ? 1 : 0))
    });
  }

  // ── Stream de mensagens ──

  stream(channelId) {
    if (!this.streams[channelId]) {
      this.streams[channelId] = {
        messages: [],
        hasMore: true,
        loading: false,
        loadedInitial: false,
        dividerAfterId: null,
        stale: false
      };
    }
    return this.streams[channelId];
  }

  /**
   * Carrega a página mais nova e congela o divisor de não lidas no marcador de
   * leitura da entrada.
   */
  async loadChannel(channelId) {
    var _channel$lastReadMess2, _channel$unreadCount3;
    const stream = this.stream(channelId);

    // Pintado do snapshot e já na tela, então nada é aguardado; a página do servidor
    // o substitui ao chegar.
    if (stream.stale && stream.loadedInitial && !stream.loading) {
      stream.stale = false;
      this.revalidateStream(channelId).catch(() => {});
      return;
    }
    if (stream.loadedInitial || stream.loading) return;
    const channel = this.channel(channelId);
    const lastRead = (_channel$lastReadMess2 = channel == null ? void 0 : channel.lastReadMessageId()) != null ? _channel$lastReadMess2 : 0;

    // Divisor só quando há algo não lido abaixo dele.
    stream.dividerAfterId = lastRead > 0 && ((_channel$unreadCount3 = channel == null ? void 0 : channel.unreadCount()) != null ? _channel$unreadCount3 : 0) > 0 ? lastRead : null;
    await this.fetchPage(channelId);
    stream.loadedInitial = true;
  }

  /**
   * Busca uma página mais antiga que a carregada. A API ordena do mais novo para o
   * mais antigo, então a página é invertida antes de ir para o início.
   */
  async fetchPage(channelId) {
    await this.fetchInto(this.stream(channelId), {
      channel: channelId
    }, this.goneHandler(channelId));
  }

  /**
   * Pagina um stream para trás a partir de um filtro base. Compartilhado por canal e
   * tópico, que são janelas sobre o mesmo endpoint, para não mostrarem históricos
   * diferentes.
   */
  async fetchInto(stream, filter, errorHandler) {
    if (stream.loading || !stream.hasMore) return;
    stream.loading = true;
    try {
      const oldest = stream.messages[0];
      const results = await app.store.find('chat-messages', {
        filter: _objectSpread(_objectSpread({}, filter), oldest ? {
          lessThan: Number(oldest.id())
        } : {}),
        sort: '-id',
        page: {
          limit: PAGE_SIZE
        }
      }, undefined, errorHandler ? {
        errorHandler
      } : undefined);
      const page = (Array.isArray(results) ? results : []).slice().reverse();
      if (page.length < PAGE_SIZE) stream.hasMore = false;

      // Merge em vez de concat: um push pode já ter inserido uma destas, e linha
      // duplicada é pior que ausente.
      const known = new Set(stream.messages.map(msg => msg.id()));
      const fresh = page.filter(msg => !known.has(msg.id()));
      stream.messages = [...fresh, ...stream.messages];
      this.sortStream(stream);
      this.scheduleSnapshot();
    } finally {
      stream.loading = false;
      m.redraw();
    }
  }

  /**
   * Garante que uma mensagem está na janela carregada do canal, para poder rolar
   * até ela (fixada ou citação antigas). Pagina a lacuna entre a linha mais antiga e
   * o alvo numa só passada, mantendo a janela contígua. Resolve se a mensagem está
   * lá; resposta de tópico ou linha excluída nunca está.
   */
  async revealMessage(channelId, messageId) {
    const stream = this.stream(channelId);
    const has = () => stream.messages.some(message => Number(message.id()) === messageId);
    if (has()) return true;
    if (!stream.loadedInitial) await this.loadChannel(channelId);
    for (let waited = 0; stream.loading && waited < 100; waited++) {
      await new Promise(resolve => setTimeout(resolve, 50));
    }
    if (has()) return true;
    if (stream.loading) return false;
    stream.loading = true;
    try {
      for (let i = 0; i < GAP_MAX_PAGES && !has(); i++) {
        const oldest = stream.messages[0];
        if (oldest && Number(oldest.id()) < messageId) break;
        const results = await app.store.find('chat-messages', {
          filter: _objectSpread({
            channel: channelId,
            greaterThan: messageId - 1
          }, oldest ? {
            lessThan: Number(oldest.id())
          } : {}),
          sort: '-id',
          page: {
            limit: GAP_PAGE_SIZE
          }
        });
        const page = (Array.isArray(results) ? results : []).slice().reverse();
        const known = new Set(stream.messages.map(msg => msg.id()));
        stream.messages = [...page.filter(msg => !known.has(msg.id())), ...stream.messages];
        this.sortStream(stream);
        if (page.length < GAP_PAGE_SIZE) break;
      }
      this.scheduleSnapshot();
    } catch (_unused8) {
      return false;
    } finally {
      stream.loading = false;
      m.redraw();
    }
    return has();
  }

  /**
   * Troca uma janela semeada pelo snapshot pela página mais nova do servidor, sem
   * await porque a conversa já está desenhada. Dentro do intervalo da página o
   * servidor manda: linhas que não voltam foram excluídas e saem. Linhas mais antigas
   * não foram consultadas e ficam.
   *
   * Se falhar, o snapshot fica sem revalidar; os cursores `greaterThan` e
   * `updatedSince` do poller o alcançam no próximo tick.
   */
  async revalidateStream(channelId) {
    const stream = this.stream(channelId);
    if (stream.loading) return;
    stream.loading = true;
    try {
      const results = await app.store.find('chat-messages', {
        filter: {
          channel: channelId
        },
        sort: '-id',
        page: {
          limit: PAGE_SIZE
        }
      }, undefined, {
        errorHandler: this.goneHandler(channelId)
      });
      const fresh = (Array.isArray(results) ? results : []).slice().reverse();
      const complete = fresh.length < PAGE_SIZE;
      const oldest = fresh[0] ? Number(fresh[0].id()) : 0;
      const kept = complete ? [] : stream.messages.filter(msg => Number(msg.id()) < oldest);
      stream.messages = [...kept, ...fresh];
      this.sortStream(stream);
      if (complete) stream.hasMore = false;
      this.scheduleSnapshot();
    } finally {
      stream.loading = false;
      m.redraw();
    }
  }

  // ── Tópicos ──

  /**
   * Janela de mensagens própria de um tópico, por id. Separada de `streams` porque o
   * canal mostra o tópico só como indicador sob a raiz; as respostas nunca estão
   * naquela janela.
   */
  threadStream(threadId) {
    if (!this.threadStreams[threadId]) {
      this.threadStreams[threadId] = {
        messages: [],
        hasMore: true,
        loading: false,
        loadedInitial: false,
        // Tópicos não têm marcador de leitura próprio, então não há divisor.
        dividerAfterId: null,
        stale: false
      };
    }
    return this.threadStreams[threadId];
  }
  async loadThread(threadId) {
    const stream = this.threadStream(threadId);
    if (stream.loadedInitial || stream.loading) return;
    await this.fetchThreadPage(threadId);
    stream.loadedInitial = true;
  }
  async fetchThreadPage(threadId) {
    await this.fetchInto(this.threadStream(threadId), {
      thread: threadId
    });
  }

  /** Carrega o registro do tópico, para o título e a contagem do painel. */
  async findThread(threadId) {
    const known = app.store.getById('chat-threads', String(threadId));
    if (known) return known;
    try {
      var _ref7;
      return (_ref7 = await app.store.find('chat-threads', String(threadId))) != null ? _ref7 : null;
    } catch (_unused9) {
      return null;
    }
  }
  closeThread() {
    this.activeThreadId = null;
  }

  /**
   * Fecha o que estiver cobrindo a conversa na gaveta. Chamado ao trocar de canal:
   * uma busca ou lista de fixadas pertence ao canal de onde foi aberta.
   */
  closeOverlays() {
    this.showPinned = false;
    this.showSearch = false;
    this.showThreads = false;
    this.activeThreadId = null;
  }

  // ── Fixadas ──

  /**
   * Busca a fixada mais nova acima da janela carregada, uma vez por canal.
   * `latestPinned()` já responde pela janela quando pode; cada busca é uma leitura
   * completa de mensagem (nove policies e quatro resumos de relação).
   *
   * `channelId in this.pinnedPreviews` em vez de truthiness: `null` é "perguntado,
   * nada fixado" e não deve ser repetido. O realtime mantém a resposta correta (ver
   * `onMessageChanged`).
   */
  async loadPinnedPreview(channelId, force) {
    if (force === void 0) {
      force = false;
    }
    if (!force && channelId in this.pinnedPreviews) {
      // Do snapshot: já exibido, perguntado de novo por trás.
      if (this.pinnedStale.delete(channelId)) {
        this.loadPinnedPreview(channelId, true).catch(() => {});
      }
      return;
    }
    const inFlight = this.pinnedInFlight.get(channelId);
    if (inFlight) return inFlight;
    const request = this.fetchPinnedPreview(channelId).finally(() => this.pinnedInFlight.delete(channelId));
    this.pinnedInFlight.set(channelId, request);
    return request;
  }
  async fetchPinnedPreview(channelId) {
    try {
      var _ref8, _readTotal;
      const results = await app.store.find('chat-messages', {
        filter: {
          channel: channelId,
          pinned: true,
          includeThreadReplies: true
        },
        sort: '-pinnedAt',
        page: {
          limit: 1
        }
      });
      this.pinnedPreviews[channelId] = (_ref8 = Array.isArray(results) ? results[0] : null) != null ? _ref8 : null;

      // Total de fixadas, que decide se a barra oferece a lista completa. Vem nesta
      // mesma resposta: o json-api-server põe a contagem em toda coleção paginada.
      this.pinnedTotals[channelId] = (_readTotal = readTotal(results)) != null ? _readTotal : 0;
      this.scheduleSnapshot();
    } catch (_unused0) {
      // Barra ausente não vale um erro; a fixação ainda aparece na linha.
      this.pinnedPreviews[channelId] = null;
      this.pinnedTotals[channelId] = 0;
    } finally {
      m.redraw();
    }
  }

  /**
   * Apaga a fixada em cache do canal, para o próximo acesso perguntar ao servidor.
   * Para mensagem fixada acima da janela, que este cliente não tem para atualizar.
   */
  invalidatePinnedPreview(channelId) {
    delete this.pinnedPreviews[channelId];
    delete this.pinnedTotals[channelId];
  }

  /**
   * Quantas fixadas o canal tem: o maior entre a contagem do servidor e as fixadas na
   * janela, pois a contagem não vê fixações posteriores e a janela não vê as de cima.
   *
   * Decide se a barra merece um segundo controle: com uma fixada, a barra já a
   * mostra e o clique salta até ela.
   */
  pinnedCount(channelId) {
    var _this$pinnedTotals$ch2;
    const known = new Set();
    const preview = this.pinnedPreviews[channelId];
    if (preview != null && preview.isPinned() && preview.id()) known.add(String(preview.id()));
    for (const message of (_this$streams$channel = (_this$streams$channel2 = this.streams[channelId]) == null ? void 0 : _this$streams$channel2.messages) != null ? _this$streams$channel : []) {
      var _this$streams$channel, _this$streams$channel2;
      if (message.isPinned() && !message.isDeleted() && message.id()) {
        known.add(String(message.id()));
      }
    }
    return Math.max((_this$pinnedTotals$ch2 = this.pinnedTotals[channelId]) != null ? _this$pinnedTotals$ch2 : 0, known.size);
  }

  /**
   * Mensagem que a barra de fixadas mostra, calculada sobre a janela carregada e a
   * prévia buscada; o `pinnedAt` mais novo vence. Fixar algo visível atualiza a
   * barra sem refetch, e desafixar a mensagem exibida a remove.
   */
  latestPinned(channelId) {
    const candidates = [];
    const preview = this.pinnedPreviews[channelId];
    if (preview && preview.isPinned()) candidates.push(preview);
    for (const message of (_this$streams$channel3 = (_this$streams$channel4 = this.streams[channelId]) == null ? void 0 : _this$streams$channel4.messages) != null ? _this$streams$channel3 : []) {
      var _this$streams$channel3, _this$streams$channel4;
      if (message.isPinned() && !message.isDeleted()) candidates.push(message);
    }
    if (candidates.length === 0) return null;
    return candidates.reduce((newest, message) => {
      var _message$pinnedAt$get, _message$pinnedAt, _newest$pinnedAt$getT, _newest$pinnedAt;
      return ((_message$pinnedAt$get = (_message$pinnedAt = message.pinnedAt()) == null ? void 0 : _message$pinnedAt.getTime()) != null ? _message$pinnedAt$get : 0) > ((_newest$pinnedAt$getT = (_newest$pinnedAt = newest.pinnedAt()) == null ? void 0 : _newest$pinnedAt.getTime()) != null ? _newest$pinnedAt$getT : 0) ? message : newest;
    });
  }

  /** Painel de fixadas, busca e painel de tópico dividem um slot. */
  togglePinned() {
    this.showPinned = !this.showPinned;
    if (this.showPinned) {
      this.activeThreadId = null;
      this.showSearch = false;
      this.showThreads = false;
    }
  }

  /** Busca, no slot de sobreposição da gaveta. Ver `showSearch`. */
  toggleSearch() {
    this.showSearch = !this.showSearch;
    if (this.showSearch) {
      this.activeThreadId = null;
      this.showPinned = false;
      this.showThreads = false;
    }
  }
  toggleThreads() {
    this.showThreads = !this.showThreads;
    if (this.showThreads) {
      this.activeThreadId = null;
      this.showPinned = false;
      this.showSearch = false;
    }
  }

  /** Cronológico, por id; ids são monotônicos por canal. */
  sortStream(stream) {
    stream.messages.sort((a, b) => Number(a.id()) - Number(b.id()));
  }

  /**
   * Insere ou substitui uma mensagem no stream do canal. Usado pelo envio e pelo
   * realtime, para ordenação e deduplicação ficarem num só lugar.
   */
  upsertMessage(message) {
    const channelId = message.channelId();
    if (!channelId) return;
    const threadId = message.threadId();

    // Resposta de tópico vai para o painel do tópico aberto. Decidido aqui para
    // realtime e envio não divergirem.
    if (threadId) {
      this.insertInto(this.threadStreams[threadId], message, true);
    }

    // Resposta de tópico não entra na janela do canal: o filtro da API mantém raízes
    // e descarta respostas. Atualizar uma linha já presente é seguro (é a raiz
    // ganhando tópico). Canal nunca aberto não ganha stream; o badge basta.
    this.insertInto(this.streams[channelId], message, !threadId || this.isThreadRoot(message));
    this.scheduleSnapshot();
  }
  isThreadRoot(message) {
    // `false` quer dizer relação não carregada, e mensagem sem tópico não é raiz. O
    // broadcast de tópico liga a raiz ao chegar, então isto só ocorre para respostas
    // e raízes fora da janela.
    const thread = message.thread();
    if (!thread) return false;
    return thread.originalMessageId() === Number(message.id());
  }
  insertInto(stream, message, appendIfNew) {
    if (!stream) return;
    const index = stream.messages.findIndex(msg => msg.id() === message.id());
    if (index >= 0) {
      stream.messages[index] = message;
      return;
    }
    if (!appendIfNew) return;
    stream.messages.push(message);
    this.sortStream(stream);
  }
  removeMessage(channelId, messageId) {
    const stream = this.streams[channelId];
    if (!stream) return;
    stream.messages = stream.messages.filter(msg => Number(msg.id()) !== messageId);
    this.scheduleSnapshot();
  }

  /**
   * O mesmo, para o painel do tópico. Separado porque streams de tópico ficam em
   * outro mapa: passar um id de tópico a `removeMessage` buscaria entre os canais e
   * poderia apagar a conversa errada.
   */
  removeThreadMessage(threadId, messageId) {
    const stream = this.threadStreams[threadId];
    if (!stream) return;
    stream.messages = stream.messages.filter(msg => Number(msg.id()) !== messageId);
  }

  // ── Envio ──

  /**
   * Envia uma mensagem e retorna o model salvo. O composer é limpo antes da resposta
   * para a digitação seguir; se falhar, o conteúdo volta.
   */
  async send(channelId, content, options) {
    var _options$threadId, _options$threadId2;
    if (options === void 0) {
      options = {};
    }
    const trimmed = content.trim();
    const uploadKey = this.draftKey(channelId, (_options$threadId = options.threadId) != null ? _options$threadId : null);
    const uploadIds = this.pendingUploadsFor(channelId, (_options$threadId2 = options.threadId) != null ? _options$threadId2 : null).map(u => Number(u.id()));
    if (!trimmed && uploadIds.length === 0) return null;
    const token = "pending-".concat(++this.pendingSeq);
    this.pending.set(token, {
      channelId,
      token
    });
    try {
      var _options$threadId3, _options$replyToId, _options$createThread, _options$threadId4, _options$threadId5;
      const payload = await app.request({
        method: 'POST',
        url: "".concat(app.forum.attribute('apiUrl'), "/chat-messages"),
        body: {
          data: {
            type: 'chat-messages',
            attributes: {
              channelId,
              content: trimmed,
              threadId: (_options$threadId3 = options.threadId) != null ? _options$threadId3 : null,
              replyToId: (_options$replyToId = options.replyToId) != null ? _options$replyToId : null,
              createThread: (_options$createThread = options.createThread) != null ? _options$createThread : false,
              uploadIds
            }
          }
        }
      });
      const message = app.store.pushPayload(payload);
      this.upsertMessage(message);
      delete this.pendingUploadsByKey[uploadKey];
      this.clearContext(channelId, (_options$threadId4 = options.threadId) != null ? _options$threadId4 : null);

      // Quem envia está em dia por definição.
      const channel = this.channel(channelId);
      if (channel) {
        channel.pushAttributes({
          unreadCount: 0,
          lastReadMessageId: Number(message.id())
        });
      }
      this.clearDraft(channelId, (_options$threadId5 = options.threadId) != null ? _options$threadId5 : null);
      return message;
    } finally {
      this.pending.delete(token);
      m.redraw();
    }
  }
  hasPendingSends(channelId) {
    for (const entry of this.pending.values()) {
      if (entry.channelId === channelId) return true;
    }
    return false;
  }

  // ── Estado de leitura ──

  /**
   * Marca o canal como lido até a mensagem carregada mais nova. Fire-and-forget: um
   * recibo de leitura falho não vale alerta, e a próxima chamada leva um marcador
   * mais novo.
   *
   * Não depende de `hasUnread()`: mensagem que chega no canal aberto é marcada lida
   * sem nunca ganhar badge (`bumpChannel` chama direto), e os recibos de leitura
   * leem o marcador. A comparação abaixo é a trava certa, a mesma que o servidor usa
   * para decidir se transmite.
   */
  markRead(channelId) {
    var _channel$lastReadMess3;
    const channel = this.channel(channelId);
    if (!channel) return;
    const stream = this.streams[channelId];
    const newest = stream == null ? void 0 : stream.messages[stream.messages.length - 1];
    const upTo = newest ? Number(newest.id()) : channel.lastMessageId();
    if (!upTo) return;
    if (((_channel$lastReadMess3 = channel.lastReadMessageId()) != null ? _channel$lastReadMess3 : 0) >= upTo) return;
    channel.pushAttributes({
      unreadCount: 0,
      unreadMentionsCount: 0,
      lastReadMessageId: upTo
    });
    this.scheduleSnapshot();
    app.request({
      method: 'POST',
      url: "".concat(app.forum.attribute('apiUrl'), "/chat-channels/").concat(channelId, "/read"),
      body: {
        data: {
          attributes: {
            lastReadMessageId: upTo
          }
        }
      },
      errorHandler: this.goneHandler(channelId)
    }).catch(() => {
      // Engolido de propósito; ver acima.
    });
  }

  // ── Rascunhos ──

  draftKey(channelId, threadId) {
    if (threadId === void 0) {
      threadId = null;
    }
    return threadId ? "".concat(channelId, ":").concat(threadId) : String(channelId);
  }
  pendingUploadsFor(channelId, threadId) {
    var _this$pendingUploadsB;
    if (threadId === void 0) {
      threadId = null;
    }
    return (_this$pendingUploadsB = this.pendingUploadsByKey[this.draftKey(channelId, threadId)]) != null ? _this$pendingUploadsB : [];
  }
  addPendingUpload(channelId, threadId, upload) {
    var _this$pendingUploadsB2;
    const key = this.draftKey(channelId, threadId);
    this.pendingUploadsByKey[key] = [...((_this$pendingUploadsB2 = this.pendingUploadsByKey[key]) != null ? _this$pendingUploadsB2 : []), upload];
  }
  removePendingUpload(channelId, threadId, id) {
    var _this$pendingUploadsB3;
    const key = this.draftKey(channelId, threadId);
    this.pendingUploadsByKey[key] = ((_this$pendingUploadsB3 = this.pendingUploadsByKey[key]) != null ? _this$pendingUploadsB3 : []).filter(u => Number(u.id()) !== id);
  }

  // ── Contexto do composer (resposta / edição), por escopo ──

  replyingTo(channelId, threadId) {
    var _this$replyTargets$th;
    if (threadId === void 0) {
      threadId = null;
    }
    return (_this$replyTargets$th = this.replyTargets[this.draftKey(channelId, threadId)]) != null ? _this$replyTargets$th : null;
  }
  editing(channelId, threadId) {
    var _this$editTargets$thi;
    if (threadId === void 0) {
      threadId = null;
    }
    return (_this$editTargets$thi = this.editTargets[this.draftKey(channelId, threadId)]) != null ? _this$editTargets$thi : null;
  }

  /** Se a resposta preparada começou como ramificação, não como resposta. */
  branchingFrom(channelId, threadId) {
    if (threadId === void 0) {
      threadId = null;
    }
    return this.branchTargets[this.draftKey(channelId, threadId)] === true;
  }

  /**
   * Responder e editar se excluem dentro de um escopo. `branch` indica que veio de
   * "responder em tópico"; só o composer lê, para decidir se o envio abre um tópico
   * (ver ChatComposer.submit()).
   */
  setReplyingTo(channelId, message, threadId, branch) {
    if (threadId === void 0) {
      threadId = null;
    }
    if (branch === void 0) {
      branch = false;
    }
    const key = this.draftKey(channelId, threadId);
    delete this.editTargets[key];
    delete this.branchTargets[key];
    if (message) {
      this.replyTargets[key] = message;
      if (branch) this.branchTargets[key] = true;
    } else {
      delete this.replyTargets[key];
    }
  }
  setEditing(channelId, message, threadId) {
    if (threadId === void 0) {
      threadId = null;
    }
    const key = this.draftKey(channelId, threadId);
    delete this.replyTargets[key];
    delete this.branchTargets[key];
    if (message) {
      this.editTargets[key] = message;
    } else {
      delete this.editTargets[key];
    }
  }
  clearContext(channelId, threadId) {
    if (threadId === void 0) {
      threadId = null;
    }
    const key = this.draftKey(channelId, threadId);
    delete this.replyTargets[key];
    delete this.editTargets[key];
    delete this.branchTargets[key];
  }
  draft(channelId, threadId) {
    var _this$drafts$this$dra;
    if (threadId === void 0) {
      threadId = null;
    }
    return (_this$drafts$this$dra = this.drafts[this.draftKey(channelId, threadId)]) != null ? _this$drafts$this$dra : '';
  }
  setDraft(channelId, content, threadId) {
    if (threadId === void 0) {
      threadId = null;
    }
    const key = this.draftKey(channelId, threadId);
    if (content.trim() === '') {
      delete this.drafts[key];
    } else {
      this.drafts[key] = content;
    }
    this.persistDraft(channelId, threadId, content);
  }
  clearDraft(channelId, threadId) {
    if (threadId === void 0) {
      threadId = null;
    }
    delete this.drafts[this.draftKey(channelId, threadId)];
    this.persistDraft(channelId, threadId, '');
  }
  /**
   * Com debounce para uma tecla não virar request. Rascunhos ficam no servidor para
   * seguir o usuário entre dispositivos, mas não valem uma gravação por caractere.
   */
  persistDraft(channelId, threadId, content) {
    if (this.draftTimer !== null) window.clearTimeout(this.draftTimer);
    this.draftTimer = window.setTimeout(() => {
      app.request({
        method: 'POST',
        url: "".concat(app.forum.attribute('apiUrl'), "/chat/drafts"),
        body: {
          data: {
            attributes: {
              channelId,
              threadId,
              content
            }
          }
        }
      }).catch(() => {});
    }, 1200);
  }

  /**
   * Restaura os rascunhos, no máximo uma vez por sessão. Rebuscar a cada visita
   * competia com uma gravação ainda não concluída e podia trazer a revisão anterior.
   */
  async loadDrafts() {
    // Rascunhos são da conta e o endpoint é autenticado. A trava fica aqui, não nos
    // três pontos de chamada: um visitante receberia 401 como alerta de erro.
    if (!app.session.user) return;
    if (this.draftsRequest) return this.draftsRequest;
    if (this.draftsLoaded) return;
    this.draftsRequest = this.fetchDrafts().finally(() => {
      this.draftsRequest = null;
    });
    return this.draftsRequest;
  }
  async fetchDrafts() {
    try {
      const payload = await app.request({
        method: 'GET',
        url: "".concat(app.forum.attribute('apiUrl'), "/chat/drafts")
      });
      for (const row of (_payload$data = payload.data) != null ? _payload$data : []) {
        var _payload$data, _row$attributes2;
        const _ref9 = (_row$attributes2 = row.attributes) != null ? _row$attributes2 : {},
          channelId = _ref9.channelId,
          threadId = _ref9.threadId,
          content = _ref9.content;
        if (channelId && content) {
          this.drafts[this.draftKey(Number(channelId), threadId ? Number(threadId) : null)] = content;
        }
      }
      this.draftsLoaded = true;
    } catch (_unused1) {
      // Rascunho é conveniência e não pode travar a UI. Não marca como carregado, para a
      // próxima visita tentar de novo.
    }
  }

  // ── Digitação ──

  /** Quem está digitando no canal, sem as entradas vencidas. */
  typistsIn(channelId) {
    const entries = this.typing[channelId];
    if (!entries) return [];
    const now = Date.now();
    const names = [];
    for (const _ref0 of Object.entries(entries)) {
      var _ref1 = _slicedToArray(_ref0, 2);
      const userId = _ref1[0];
      const entry = _ref1[1];
      if (entry.expiresAt <= now) {
        window.clearTimeout(entry.timer);
        delete entries[Number(userId)];
        continue;
      }
      names.push(entry.username);
    }
    return names;
  }
  noteTyping(channelId, userId, username, typing, expiresIn) {
    if (expiresIn === void 0) {
      expiresIn = 6;
    }
    if (!this.typing[channelId]) this.typing[channelId] = {};

    // O que vier a seguir substitui esta entrada, então o redesenho pendente fica
    // velho; senão cada tecla empilharia um timer.
    const previous = this.typing[channelId][userId];
    if (previous) window.clearTimeout(previous.timer);
    if (!typing) {
      delete this.typing[channelId][userId];
      return;
    }

    // Um pouco depois do prazo, para o redesenho ler uma entrada já vencida.
    const timer = window.setTimeout(() => m.redraw(), expiresIn * 1000 + 100);
    this.typing[channelId][userId] = {
      username,
      expiresAt: Date.now() + expiresIn * 1000,
      timer
    };
  }

  /**
   * Remove quem digitava na hora: acabou de enviar. Esperar expirar deixaria
   * "X está digitando…" embaixo da mensagem de X.
   */
  clearTyping(channelId, userId) {
    this.noteTyping(channelId, userId, '', false);
  }
  /** Avisa que está digitando, no máximo a cada 3s; sem isso seria um request por tecla. */
  announceTyping(channelId) {
    const now = Date.now();
    if (now - this.typingSentAt < 3000) return;
    this.typingSentAt = now;
    app.request({
      method: 'POST',
      url: "".concat(app.forum.attribute('apiUrl'), "/chat/typing"),
      body: {
        data: {
          attributes: {
            channelId,
            typing: true
          }
        }
      },
      // Fire-and-forget: um sinal recusado (canal fechado, leitor removido) não vale
      // alerta a cada tecla. O push do canal ou a próxima leitura resolvem.
      errorHandler: () => {}
    }).catch(() => {});
  }

  // ── Seleção ──

  toggleSelecting(on) {
    this.selecting = on != null ? on : !this.selecting;
    if (!this.selecting) this.selected.clear();
  }
  toggleSelected(messageId) {
    if (this.selected.has(messageId)) {
      this.selected.delete(messageId);
    } else {
      this.selected.add(messageId);
    }
  }

  /**
   * Transforma a seleção em transcrição. No servidor para as regras de citação
   * baterem com as do arquivamento.
   */
  async transcript(format) {
    var _payload$data$attribu, _payload$data2;
    if (format === void 0) {
      format = 'markup';
    }
    if (this.selected.size === 0) return '';
    const payload = await app.request({
      method: 'POST',
      url: "".concat(app.forum.attribute('apiUrl'), "/chat/transcript"),
      body: {
        data: {
          attributes: {
            messageIds: Array.from(this.selected),
            format
          }
        }
      }
    });
    return (_payload$data$attribu = (_payload$data2 = payload.data) == null || (_payload$data2 = _payload$data2.attributes) == null ? void 0 : _payload$data2.content) != null ? _payload$data$attribu : '';
  }
}
flarum.reg.add('ramon-chat', 'forum/state/ChatState', ChatState);